import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { supabase } from '../supabaseClient';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import PortalToggle from './PortalToggle';
import AutoSizeTextarea from './AutoSizeTextarea';
import Badge from './Badge';
import ComplianceConfigFields, {
  seedComplianceFields, normaliseComplianceFields,
} from './ComplianceConfigFields';
import NotifySuppliersModal from './NotifySuppliersModal';
import { fetchNotifiableSuppliers } from '../lib/notifyDocument';
import { fmtDate } from '../lib/dates';
import '../lib/quill-theme.css';

// Quill toolbar — same minimal config as AddBrief / AmendBriefPanel so the
// formatting suppliers see in their acknowledgement is consistent with the
// rest of Fusion 2K's authored content. Bold / italic / underline + lists is
// what's actually been asked for in practice; everything else is noise.
const ACK_QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};
const ACK_QUILL_FORMATS = ['bold', 'italic', 'underline', 'list'];

// PDF upload guardrails. 10 MB cap matches brief attachments; PDF-only by
// design (the supplier-side modal renders via <iframe>, which is reliable
// for PDF but not for DOCX). The bucket is shared with supplier uploads —
// admin-authored content lands under a __fusion-documents/ path prefix
// so it sorts away from supplier files if anyone browses the bucket.
const ACK_PDF_MAX_BYTES = 10 * 1024 * 1024;
const ACK_PDF_BUCKET = 'supplier-uploads';
const ACK_PDF_PATH_PREFIX = '__fusion-documents';

// Document configuration modal — opens from the Configure button on any
// row in FusionDocuments. Edits the basics (name / category / description)
// + visibility toggles + file replacement (stub) for a single
// fusion_documents row.
//
// Parent owns the save/category-change wiring; this modal manages local
// form state, validation, and the unsaved-changes guard. onSave receives
// the full patch; the parent decides which side-effects fire (category
// change confirm, T&C hide warning) before persisting.
//
// Three sections:
//
//   1. Basics      — name, category (incl. category-change confirm),
//                    description
//   2. Visibility  — show-in-portal + notify-on-update toggles. Replaced
//                    with a notice for compliance docs (Risk-Flag-driven);
//                    show-in-portal is locked off for internal_reference.
//   3. File        — current file preview + Replace button (stub for now).
//
// Save fires onSave with { fields }. Parent handles category-change
// confirms (Internal Reference → Supplier Enhancement, etc.) before
// committing — the modal stays open until onSave resolves so an error
// can be surfaced inline.

const CATEGORIES = [
  { value: 'terms_conditions',     label: 'Terms & Conditions' },
  { value: 'compliance',           label: 'Compliance' },
  { value: 'supplier_enhancement', label: 'Supplier Enhancement' },
  { value: 'internal_reference',   label: 'Internal Reference' },
];

const seedFromDoc = (doc) => {
  if (!doc) return null;
  return {
    document_name: doc.document_name || '',
    document_type: doc.document_type || 'internal_reference',
    description: doc.description || '',
    visible_in_portal: doc.document_type === 'internal_reference' ? false : !!doc.visible_in_portal,
    notify_on_update: !!doc.notify_on_update,
    badge_category_id: doc.badge_category_id || null,
    badge_tier_awarded: doc.badge_tier_awarded || null,
    // Acknowledge satisfaction type — compliance-only fields. Default
    // satisfied_by='upload' so flipping is opt-in per-doc. lifecycle /
    // source / content_richtext stay null until the admin enables
    // Acknowledge; the form below shows / hides their controls based
    // on satisfied_by so the modal stays compact on legacy docs.
    satisfied_by: doc.satisfied_by || 'upload',
    lifecycle: doc.lifecycle || null,
    source: doc.source || null,
    content_richtext: doc.content_richtext || '',
    content_file_url: doc.content_file_url || null,
    content_file_name: doc.content_file_name || null,
  };
};

export default function DocumentConfigModal({
  open,
  document,
  categories = [],       // data-driven document_categories (key, label, behaviour)
  reminderRules = [],    // this doc's existing document_reminder_rules
  onClose,
  onSave,
  onReplaceFile,        // optional; if absent, Replace button is stubbed
}) {
  const [fields, setFields] = useState(null);
  // Compliance config (validity/expiry/accepted types/enforcement) + reminder
  // rules, edited via the shared ComplianceConfigFields. Only persisted for
  // required-behaviour docs.
  const [complianceFields, setComplianceFields] = useState(null);
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [notifyPicker, setNotifyPicker] = useState(null); // { suppliers } when the notify picker is open
  // PDF upload state — null when idle; the modal disables Save while a
  // file is mid-upload so the admin can't hit Save with stale fields.
  // uploadError surfaces inline next to the file picker so it doesn't
  // collide with the form-wide inlineError below.
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfUploadError, setPdfUploadError] = useState(null);
  const pdfInputRef = useRef(null);
  // Loaded once on open — drives the badge category dropdown when admin
  // ticks "Contributes to badge". Tiny query (only ever a handful of
  // rows) so we don't bother caching across opens.
  const [badgeCategories, setBadgeCategories] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('badge_categories')
        .select('id, slug, name, bronze_name, silver_name, gold_name')
        .eq('is_active', true)
        .order('name');
      if (!cancelled) setBadgeCategories(data || []);
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setFields(seedFromDoc(document));
    setComplianceFields(seedComplianceFields(document));
    setRules((reminderRules || []).map(r => ({ ...r })));
    setInlineError(null);
    setConfirmCancel(false);
    // reminderRules is intentionally excluded — it's re-read on each open via
    // the effect body; adding it re-seeds mid-edit on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document]);

  const baseline = useMemo(
    () => JSON.stringify({ f: seedFromDoc(document), c: seedComplianceFields(document), r: reminderRules || [] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [document]);
  const isDirty = useMemo(
    () => fields && JSON.stringify({ f: fields, c: complianceFields, r: rules }) !== baseline,
    [fields, complianceFields, rules, baseline]);

  const requestClose = useCallback(() => {
    if (isDirty) setConfirmCancel(true);
    else onClose?.();
  }, [isDirty, onClose]);

  // PDF upload handler — fires off the file picker change event. Uploads
  // to supabase storage via the service-role client (admins write to a
  // shared bucket; RLS would otherwise block direct anon writes). On
  // success, writes back content_file_url + content_file_name into form
  // state so Save persists them onto fusion_documents. Defined ABOVE
  // the early return so React's rules-of-hooks stay happy on first
  // render (open=false).
  const handlePdfPick = useCallback(async (e) => {
    const file = e.target?.files?.[0];
    // Reset the input so picking the same file twice re-fires onChange.
    if (e.target) e.target.value = '';
    if (!file) return;
    setPdfUploadError(null);
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setPdfUploadError('PDF only. The supplier-side viewer renders via <iframe>, which doesn’t reliably handle other formats.');
      return;
    }
    if (file.size > ACK_PDF_MAX_BYTES) {
      setPdfUploadError(`That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${ACK_PDF_MAX_BYTES / 1024 / 1024} MB.`);
      return;
    }
    if (!document?.id) {
      setPdfUploadError('Save the document first so we can attach the PDF to its record.');
      return;
    }
    setPdfUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${ACK_PDF_PATH_PREFIX}/${document.id}/${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from(ACK_PDF_BUCKET)
        .upload(path, file, { contentType: 'application/pdf', upsert: true });
      if (uploadError) throw new Error(uploadError.message);
      const { data: pub } = supabase.storage.from(ACK_PDF_BUCKET).getPublicUrl(path);
      const fileUrl = pub?.publicUrl || null;
      if (!fileUrl) throw new Error('Upload succeeded but the public URL came back empty. Try again or re-check the bucket policy.');
      setFields(prev => ({ ...prev, content_file_url: fileUrl, content_file_name: file.name }));
    } catch (err) {
      setPdfUploadError(err?.message || 'Upload failed. Try again.');
    } finally {
      setPdfUploading(false);
    }
  }, [document?.id]);

  if (!open || !fields) return null;

  const setField = (key, value) => setFields(prev => ({ ...prev, [key]: value }));
  const setComplianceField = (key, value) => setComplianceFields(prev => ({ ...prev, [key]: value }));
  // Category behaviour is data-driven now. Fall back to the seed-four keys so a
  // doc still resolves before document_categories loads.
  const behaviourOf = (key) => categories.find(c => c.key === key)?.behaviour
    || (key === 'compliance' ? 'required' : key === 'internal_reference' ? 'internal' : 'shown');
  const categoryOptions = categories.length
    ? categories.map(c => ({ value: c.key, label: c.label }))
    : CATEGORIES;
  const isCompliance = behaviourOf(fields.document_type) === 'required';
  const isInternal = behaviourOf(fields.document_type) === 'internal';
  const isSuperseded = document?.status === 'superseded';
  // Publishing a draft from here mirrors the Upload modal: required docs go live
  // via Risk Flag Config (no publish button); shown/internal can be published;
  // only shown docs can notify suppliers.
  const isDraft = document?.status === 'draft';
  const canPublish = isDraft && !isCompliance;
  const canNotify = isDraft && !isCompliance && !isInternal;

  // Superseded rows can never become visible again regardless of category
  // — the data model lets us toggle, but business rule forbids it. The
  // modal mirrors that by locking the visibility toggle.
  const portalToggleState = (() => {
    if (isCompliance) return 'locked';
    if (isInternal) return 'locked';
    if (isSuperseded) return 'locked';
    return fields.visible_in_portal ? 'on' : 'off';
  })();
  const portalToggleReason = isSuperseded
    ? 'category-locked'
    : isInternal
      ? 'category-locked'
      : 'by-risk-tier';

  const openNotifyPicker = async () => {
    const suppliers = await fetchNotifiableSuppliers();
    setNotifyPicker({ suppliers });
  };

  const doSave = async (publish = null, supplierIds = null) => {
    setInlineError(null);
    if (!fields.document_name?.trim()) {
      setInlineError('Document name is required.');
      return;
    }
    // Acknowledge validation — when an admin flips a compliance doc
    // to Acknowledge, the source content must actually be present
    // (matches the DB CHECK that requires lifecycle + source on
    // Acknowledge rows, and stops a half-configured doc reaching
    // suppliers). Richtext needs non-empty content; file needs an
    // uploaded URL.
    if (isCompliance && fields.satisfied_by === 'acknowledge') {
      if (!fields.lifecycle) {
        setInlineError('Pick a lifecycle (one-off or recurring) for the Acknowledge document.');
        return;
      }
      if (!fields.source) {
        setInlineError('Pick a source (rich text or file) for the Acknowledge document.');
        return;
      }
      if (fields.source === 'richtext' && !fields.content_richtext?.trim()) {
        setInlineError('Enter the content suppliers will read and acknowledge.');
        return;
      }
      if (fields.source === 'file' && !fields.content_file_url) {
        setInlineError('Upload the PDF suppliers will read and acknowledge.');
        return;
      }
    }
    setSaving(true);
    try {
      // Merge compliance config into the fields payload + pass reminder rules
      // (required docs only). Non-required docs send neither, so the parent
      // leaves those columns and rules untouched.
      const payloadFields = isCompliance
        ? { ...fields, ...normaliseComplianceFields(complianceFields) }
        : fields;
      await onSave?.({ fields: payloadFields, reminderRules: isCompliance ? rules : null, publish, supplierIds });
    } catch (e) {
      setInlineError(e?.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };
  const handleSave = () => doSave(null);

  const versionLabel = document?.version_value
    ? `v${document.version_value}${document.version_year ? ` · ${document.version_year}` : ''}`
    : null;
  const categoryLabel = categoryOptions.find(c => c.value === fields.document_type)?.label || fields.document_type;

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        ariaLabel={`Configure ${fields.document_name || 'document'}`}>
        <Modal.Header
          eyebrow="Configure Document"
          title={fields.document_name || 'Document'}
          sub={[versionLabel, categoryLabel].filter(Boolean).join(' · ')}
          onClose={requestClose} />

        <Modal.Body>
          {/* === BASICS === */}
          <Section label="Basics">
            <Field label="Document Name">
              <input
                type="text"
                value={fields.document_name}
                onChange={e => setField('document_name', e.target.value)}
                className={FIELD_INPUT_CLASS} />
            </Field>
            <Field label="Category">
              <Select
                value={fields.document_type}
                onChange={v => setField('document_type', v)}
                options={categoryOptions} />
            </Field>
            <Field label="Description" opt="— shown to suppliers when they upload">
              <AutoSizeTextarea
                value={fields.description}
                onChange={e => setField('description', e.target.value)}
                minRows={3}
                className={`${FIELD_INPUT_CLASS} min-h-[80px] leading-relaxed`} />
            </Field>
          </Section>

          {/* === VISIBILITY === */}
          <Section label="Visibility">
            {isCompliance ? (
              <div className="bg-[#2E8FA1]/[0.06] border border-[#2E8FA1]/30 rounded p-3 text-xs text-fusion-mid-grey leading-relaxed">
                Visibility for compliance documents is managed in <span className="text-[#2E8FA1] font-semibold">Risk Flag Configuration</span> — suppliers see this document based on their risk tier (High / Medium / Low).
              </div>
            ) : (
              <>
                <ToggleRow
                  label="Show in supplier portal"
                  sub="When on, every supplier can view and download this document from their portal."
                  state={portalToggleState}
                  lockedReason={portalToggleReason}
                  onChange={(next) => setField('visible_in_portal', next)} />
                {isInternal && (
                  <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1.5 leading-relaxed">
                    Internal Reference documents can never be shown to suppliers. Change the category above first to make this visible.
                  </div>
                )}
                {isSuperseded && (
                  <div className="font-['Metropolis'] text-[11px] text-[#7A4E10] mt-1.5 leading-relaxed">
                    Superseded versions can't be made visible. Restore this version as Active first.
                  </div>
                )}
                <div className="mt-3">
                  <SwitchToggle
                    label="Notify suppliers when this changes"
                    sub="Send an email to all suppliers when a new version is uploaded."
                    checked={fields.notify_on_update}
                    onChange={(next) => setField('notify_on_update', next)} />
                </div>
              </>
            )}
          </Section>

          {/* === SATISFACTION === */}
          {/* Compliance-only — chooses how the supplier satisfies the
              requirement. Upload (the legacy flow) vs Acknowledge
              (in-app sign-off — see Acknowledge build brief). When
              Acknowledge is on, the admin picks lifecycle + source
              and authors the content suppliers will read. The fields
              cascade: source picker only shows after lifecycle is
              set, content editor only shows after source is set, so
              the modal stays compact until the admin commits to a
              direction. */}
          {isCompliance && (
            <Section label="Satisfaction">
              <Field label="Satisfied by" opt="— how the supplier completes this requirement">
                <Select
                  value={fields.satisfied_by}
                  onChange={v => setField('satisfied_by', v)}
                  options={[
                    { value: 'upload', label: 'Upload — supplier uploads their own evidence' },
                    { value: 'download_return', label: 'Download & return — supplier signs our file offline and re-uploads' },
                    { value: 'form', label: 'Form — supplier completes a hosted form in-app' },
                    { value: 'acknowledge', label: 'Acknowledge — supplier signs off in-app' },
                  ]} />
              </Field>
              {fields.satisfied_by === 'acknowledge' && (
                <>
                  <Field label="Lifecycle" opt="— how often it needs re-acknowledging">
                    <Select
                      value={fields.lifecycle || ''}
                      onChange={v => setField('lifecycle', v || null)}
                      options={[
                        { value: '', label: 'Choose lifecycle…' },
                        { value: 'one_off', label: 'One-off — signed once (e.g. T&Cs at sign-up)' },
                        { value: 'recurring', label: 'Recurring — re-acknowledge on expiry / material version (e.g. CoC)' },
                      ]} />
                  </Field>
                  {fields.lifecycle && (
                    <Field label="Source" opt="— where the content suppliers read comes from">
                      <Select
                        value={fields.source || ''}
                        onChange={v => setField('source', v || null)}
                        options={[
                          { value: '', label: 'Choose source…' },
                          { value: 'richtext', label: 'Rich text — author content in the editor below' },
                          { value: 'file', label: 'File — upload a PDF (frozen per version)' },
                        ]} />
                    </Field>
                  )}
                  {fields.source === 'richtext' && (
                    <Field label="Content" opt="— suppliers read this before they acknowledge">
                      {/* Phase 2c: Quill rich-text editor. Same minimal
                          toolbar as the brief editor (bold / italic /
                          underline + ordered/bullet lists) — no
                          headings / colours / tables on purpose, since
                          the supplier-side viewer renders these via
                          dangerouslySetInnerHTML and a constrained
                          format keeps the output predictable. Output
                          is HTML stored as-is in content_richtext. */}
                      <div className="sp-quill">
                        <ReactQuill
                          theme="snow"
                          value={fields.content_richtext}
                          onChange={(html) => setField('content_richtext', html)}
                          modules={ACK_QUILL_MODULES}
                          formats={ACK_QUILL_FORMATS}
                          placeholder="Write the acknowledgement content here…" />
                      </div>
                      <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1.5 leading-relaxed">
                        Bold / italic / underline + ordered &amp; bulleted lists only — matches the brief editor. Output is HTML rendered verbatim on the supplier portal.
                      </div>
                    </Field>
                  )}
                  {fields.source === 'file' && (
                    <Field label="File" opt="— PDF only (DOCX not supported by the supplier viewer)">
                      <div className="bg-white border border-line rounded p-3">
                        {fields.content_file_url ? (
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-['Metropolis'] text-sm text-ink truncate">
                                {fields.content_file_name || 'Uploaded PDF'}
                              </div>
                              <a
                                href={fields.content_file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-['Metropolis'] text-[10px] tracking-[0.15em] uppercase text-[#2E8FA1] hover:text-active-green transition-colors">
                                View file ↗
                              </a>
                            </div>
                            <button
                              type="button"
                              onClick={() => pdfInputRef.current?.click()}
                              disabled={pdfUploading}
                              className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-fusion-mid-grey hover:text-active-green transition-colors disabled:opacity-50">
                              {pdfUploading ? 'Uploading…' : 'Replace'}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setField('content_file_url', null); setField('content_file_name', null); setPdfUploadError(null); }}
                              disabled={pdfUploading}
                              className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A] hover:text-[#E0290A] transition-colors disabled:opacity-50">
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-['Metropolis'] text-sm text-ink">No PDF uploaded yet</div>
                              <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 leading-relaxed">
                                PDF only, max {ACK_PDF_MAX_BYTES / 1024 / 1024} MB. Frozen on this version — bump the version to publish an updated file.
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => pdfInputRef.current?.click()}
                              disabled={pdfUploading}
                              className="bg-white border border-line rounded font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-fusion-mid-grey px-3 py-1.5 hover:border-active-green hover:text-active-green transition-colors disabled:opacity-50">
                              {pdfUploading ? 'Uploading…' : 'Choose PDF'}
                            </button>
                          </div>
                        )}
                        {/* Hidden file input — the visible button proxies
                            click() to it so we get a styled control
                            without giving up native file-picker UX. */}
                        <input
                          ref={pdfInputRef}
                          type="file"
                          accept="application/pdf,.pdf"
                          onChange={handlePdfPick}
                          className="hidden"
                          aria-label="Choose PDF to upload" />
                      </div>
                      {pdfUploadError && (
                        <div className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1.5 leading-relaxed">
                          {pdfUploadError}
                        </div>
                      )}
                    </Field>
                  )}
                </>
              )}
            </Section>
          )}

          {/* === COMPLIANCE CONFIG === */}
          {/* Validity/expiry, accepted file types, reminder rules, enforcement
              — moved here from the retired Risk Flag drawer so all document
              config lives in Fusion Documents. */}
          {isCompliance && complianceFields && (
            <div className="mb-2">
              <ComplianceConfigFields
                fields={complianceFields}
                setField={setComplianceField}
                rules={rules}
                setRules={setRules}
                satisfiedBy={fields?.satisfied_by || document?.satisfied_by} />
            </div>
          )}

          {/* === RECOGNITION AWARD (inherited from category) === */}
          {/* Badges are category-driven now — set once on the category, every
              document inherits. Read-only here to match the Upload modal. */}
          <Section label="Recognition Award">
            {(() => {
              const cat = categories.find(c => c.key === fields.document_type);
              const bc = cat?.badge_category_id ? badgeCategories.find(b => b.id === cat.badge_category_id) : null;
              return (
                <div className="flex items-center gap-3">
                  {bc && <Badge tier={cat.badge_tier_awarded || 'bronze'} size="md" categorySlug={bc.slug} />}
                  <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-relaxed">
                    {bc
                      ? <>Inherited from this category: <strong className="text-ink">{bc.name}{bc.slug === 'eco' ? ' (Acorn → Oak Status)' : ''}</strong>. </>
                      : <>No recognition award on this category. </>}
                    Set it in <span className="text-[#2E8FA1] font-semibold">Manage Categories</span> to re-link every document here.
                  </p>
                </div>
              );
            })()}
          </Section>

          {/* === FILE === */}
          <Section label="File" last>
            <div className="bg-white border border-line rounded p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-[#2E8FA1]/10 border border-[#2E8FA1]/30 flex items-center justify-center flex-shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2E8FA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-['Metropolis'] text-sm font-semibold text-ink truncate">
                  {document?.file_name || document?.document_name || 'No file uploaded'}
                </div>
                <div className="font-['Metropolis'] text-[10px] text-[#51534A] mt-0.5">
                  {document?.file_url
                    ? [
                        document.file_size_kb ? `${document.file_size_kb} KB` : null,
                        document.created_at ? `Uploaded ${fmtDate(document.created_at)}` : null,
                      ].filter(Boolean).join(' · ')
                    : 'Placeholder — no file uploaded yet'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onReplaceFile ? onReplaceFile(document) : alert('Replace file flow coming soon')}
                className="bg-white border border-line rounded font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-fusion-mid-grey px-3 py-1.5 hover:border-[#DADCCD] transition-colors">
                Replace
              </button>
            </div>
            <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-2 leading-relaxed">
              Replacing the file creates a new version. The current version is moved to history and marked Superseded.
            </div>
          </Section>

          {inlineError && (
            <div className="mt-4 font-['Metropolis'] text-xs text-[#E0290A] bg-[#E0290A]/10 border border-[#E0290A]/30 rounded px-3 py-2">
              {inlineError}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {/* Required drafts publish per risk tier in Risk Flag Config — not here. */}
            <p className="font-['Metropolis'] text-[11px] text-[#51534A] leading-snug max-w-[280px]">
              {isDraft && isCompliance
                ? 'This compliance draft goes live per risk tier in Risk Flag Configuration.'
                : ''}
            </p>
            <div className="flex items-center justify-end gap-2 ml-auto">
              <button
                type="button"
                onClick={requestClose}
                disabled={saving}
                className="bg-white border border-line rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-ink px-4 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || pdfUploading}
                title={pdfUploading ? 'Wait for the PDF upload to finish.' : undefined}
                className={`bg-white border rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase px-4 py-2 transition-colors disabled:opacity-50 ${isDraft ? 'border-line text-fusion-mid-grey hover:border-[#DADCCD]' : 'border-[#92D400] text-[#131313] bg-[#92D400] hover:bg-[#a4e810]'}`}>
                {saving && !notifyPicker ? 'Saving…' : (isDraft ? 'Save as draft' : 'Save Changes')}
              </button>
              {canPublish && (
                <button
                  type="button"
                  onClick={() => doSave('silent')}
                  disabled={saving || pdfUploading}
                  className="bg-white border border-[#92D400] text-fusion-forest rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase px-4 py-2 hover:bg-[#92D400]/10 transition-colors disabled:opacity-50">
                  Silent publish
                </button>
              )}
              {canNotify && (
                <button
                  type="button"
                  onClick={openNotifyPicker}
                  disabled={saving || pdfUploading}
                  className="bg-[#92D400] border border-[#92D400] rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-[#131313] px-4 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">
                  Notify &amp; publish
                </button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <ConfirmDialog
        open={confirmCancel}
        title="Discard unsaved changes?"
        body="You've made changes that haven't been saved. Closing this modal will discard them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={() => { setConfirmCancel(false); onClose?.(); }}
        onCancel={() => setConfirmCancel(false)} />

      <NotifySuppliersModal
        open={!!notifyPicker}
        title="Notify &amp; publish"
        intro={`Publish "${fields.document_name || 'this document'}" and email the chosen suppliers' portal users that a new document is available. Each send is logged to the supplier's Activity tab.`}
        suppliers={notifyPicker?.suppliers || []}
        busy={saving}
        confirmLabel="Publish & notify"
        onConfirm={(ids) => { setNotifyPicker(null); doSave('notify', ids); }}
        onCancel={() => setNotifyPicker(null)} />
    </>
  );
}

// ============== sub-components ==============

const FIELD_INPUT_CLASS = "w-full bg-white border border-line rounded px-3.5 py-2.5 text-ink font-['Metropolis'] text-sm outline-none focus:border-[#92D400] transition-colors";

function Section({ label, last, children }) {
  return (
    <div className={`${last ? 'mb-0' : 'mb-6'}`}>
      <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A] pb-2 mb-3.5 border-b border-line">
        {label}
      </div>
      {children}
    </div>
  );
}

function Field({ label, opt, children }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="font-['Metropolis'] text-[11px] font-semibold text-ink mb-1.5 flex items-center gap-1.5">
        <span>{label}</span>
        {opt && <span className="text-[#51534A] font-normal">{opt}</span>}
      </div>
      {children}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <div className="relative">
      <select
        className={`${FIELD_INPUT_CLASS} appearance-none pr-9 cursor-pointer`}
        value={value}
        onChange={e => onChange(e.target.value)}>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <svg
        width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#51534A" strokeWidth="2.5"
        className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
        aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
}

// Pulls the PortalToggle into a labelled-row variant. The row carries the
// descriptive copy; the toggle itself sits on the right.
function ToggleRow({ label, sub, state, lockedReason, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4 p-3 bg-white border border-line rounded">
      <div className="flex-1 min-w-0">
        <div className="font-['Metropolis'] text-xs font-semibold text-ink">{label}</div>
        {sub && <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 leading-relaxed">{sub}</div>}
      </div>
      <PortalToggle state={state} lockedReason={lockedReason} onChange={onChange} />
    </div>
  );
}

// Plain on/off switch for non-visibility toggles (notify-on-update etc.).
function SwitchToggle({ label, sub, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-full flex items-center gap-2.5 p-3 border rounded text-left transition-colors ${checked
        ? 'bg-[#92D400]/[0.04] border-[#92D400]/30'
        : 'bg-white border-line hover:border-[#DADCCD]'}`}>
      <div className={`w-8 h-[18px] rounded-full relative flex-shrink-0 transition-colors ${checked ? 'bg-[#92D400]' : 'bg-[#51534A]'}`}>
        <span
          className="absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-[#F9FCF1] transition-transform"
          style={{ transform: checked ? 'translateX(14px)' : 'translateX(0)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-['Metropolis'] text-xs font-semibold text-ink">{label}</div>
        {sub && <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 leading-relaxed">{sub}</div>}
      </div>
    </button>
  );
}
